import type { IntlShape } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { DecorationSet, type EditorView } from '@atlaskit/editor-prosemirror/view';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';

import { EMPTY_PARAGRAPH_TIMEOUT_DELAY, pluginKey } from '../placeholderPlugin';
import type { PlaceholderPlugin } from '../placeholderPluginType';

import { TYPEWRITER_TYPED_AND_DELETED_DELAY } from './constants';
import { createPlaceholderDecoration } from './decorations';
import {
	createMultiBodiedExtensionPlaceholderDecorations,
	updateMultiBodiedExtensionPlaceholderDecorations,
} from './multi-bodied-extension-placeholder-decorations';
import {
	createSourceSyncBlockPlaceholderDecorations,
	updateSourceSyncBlockPlaceholderDecorations,
} from './source-sync-block-placeholder-decorations';
import type { PlaceHolderState } from './types';
import {
	calculateUserInteractionState,
	createPlaceHolderStateFrom,
	getPlaceholderState,
} from './utils';

export default function createPlugin(
	intl: IntlShape,
	defaultPlaceholderText?: string,
	bracketPlaceholderText?: string,
	emptyLinePlaceholder?: string,
	placeholderPrompts?: string[],
	withEmptyParagraph?: boolean,
	initialIsPlaceholderHidden?: boolean,
	placeholderADF?: DocNode,
	isRovoLLMEnabled?: boolean,
	api?: ExtractInjectionAPI<PlaceholderPlugin>,
): SafePlugin | undefined {
	if (
		!defaultPlaceholderText &&
		!placeholderPrompts &&
		!bracketPlaceholderText &&
		!placeholderADF
	) {
		return;
	}

	let isDestroyed = false;
	let activeTypewriterTimeouts: (() => void)[] = [];
	const clearAllTypewriterTimeouts = () => {
		activeTypewriterTimeouts.forEach((clearFn) => clearFn());
		activeTypewriterTimeouts = [];
	};

	return new SafePlugin<PlaceHolderState>({
		key: pluginKey,
		state: {
			init: (_, state) => ({
				...createPlaceHolderStateFrom({
					isInitial: true,
					isEditorFocused: Boolean(api?.focus?.sharedState.currentState()?.hasFocus),
					editorState: state,
					isTypeAheadOpen: api?.typeAhead?.actions.isOpen,
					defaultPlaceholderText,
					bracketPlaceholderText,
					emptyLinePlaceholder,
					placeholderADF,
					placeholderPrompts,
					typedAndDeleted: false,
					userHadTyped: false,
					intl,
					withEmptyParagraph,
					isPlaceholderHidden: initialIsPlaceholderHidden,
				}),
				sourceSyncBlockPlaceholderDecorations: createSourceSyncBlockPlaceholderDecorations(
					state,
					intl,
				),
				multiBodiedExtensionPlaceholderDecorations:
					createMultiBodiedExtensionPlaceholderDecorations(state, intl),
			}),

			apply: (tr, placeholderState, _oldEditorState, newEditorState) => {
				const meta = tr.getMeta(pluginKey);
				const isEditorFocused = Boolean(api?.focus?.sharedState.currentState()?.hasFocus);

				const { userHadTyped, typedAndDeleted } = calculateUserInteractionState({
					placeholderState,
					oldEditorState: _oldEditorState,
					newEditorState,
				});

				let isPlaceholderHidden = placeholderState?.isPlaceholderHidden ?? false;
				const shouldUpdatePlaceholderHidden = meta?.isPlaceholderHidden !== undefined;
				if (shouldUpdatePlaceholderHidden) {
					isPlaceholderHidden = meta.isPlaceholderHidden;
				}

				if (meta?.placeholderText !== undefined && withEmptyParagraph) {
					const isCreateWithRovoOverride =
						!!meta.placeholderText &&
						isRovoLLMEnabled &&
						expVal('cwr_blank_object_experiment', 'isEnabled', false);
					// Only update defaultPlaceholderText from meta if we're not using ADF placeholder
					// OR when the create-with-rovo experiment is active to allow intentional non-empty placeholder overrides
					if (!placeholderADF || isCreateWithRovoOverride) {
						defaultPlaceholderText = meta.placeholderText;
					}
				}

				const newPlaceholderState = {
					...createPlaceHolderStateFrom({
						isEditorFocused,
						editorState: newEditorState,
						isTypeAheadOpen: api?.typeAhead?.actions.isOpen,
						defaultPlaceholderText: withEmptyParagraph
							? defaultPlaceholderText
							: (meta?.placeholderText ??
								placeholderState?.placeholderText ??
								defaultPlaceholderText),
						bracketPlaceholderText,
						emptyLinePlaceholder,
						placeholderADF,
						placeholderPrompts:
							meta?.placeholderPrompts ??
							placeholderState?.placeholderPrompts ??
							placeholderPrompts,
						typedAndDeleted,
						userHadTyped,
						intl,
						isPlaceholderHidden,
						withEmptyParagraph,
						showOnEmptyParagraph:
							meta?.showOnEmptyParagraph ?? placeholderState?.showOnEmptyParagraph,
					}),
					sourceSyncBlockPlaceholderDecorations: updateSourceSyncBlockPlaceholderDecorations({
						currentDecorationSet: placeholderState?.sourceSyncBlockPlaceholderDecorations,
						intl,
						newEditorState,
						oldEditorState: _oldEditorState,
						tr,
					}),
					multiBodiedExtensionPlaceholderDecorations:
						updateMultiBodiedExtensionPlaceholderDecorations({
							currentDecorationSet: placeholderState?.multiBodiedExtensionPlaceholderDecorations,
							intl,
							newEditorState,
							tr,
						}),
				};

				// Clear timeouts when hasPlaceholder becomes false
				if (!newPlaceholderState.hasPlaceholder) {
					clearAllTypewriterTimeouts();
				}

				return newPlaceholderState;
			},
		},
		props: {
			decorations(editorState) {
				// @ts-ignore quick fix which needs follow up to use standard apis
				if (editorState.collabEditPlugin$ && editorState.collabEditPlugin$.isReady !== true) {
					return;
				}
				const {
					hasPlaceholder,
					placeholderText,
					pos,
					typedAndDeleted,
					contextPlaceholderADF,
					showOnEmptyParagraph,
					sourceSyncBlockPlaceholderDecorations,
					multiBodiedExtensionPlaceholderDecorations,
				} = getPlaceholderState(editorState);

				// Decorations is still called after plugin is destroyed
				// So we need to make sure decorations is not called if plugin has been destroyed to prevent the placeholder animations' setTimeouts called infinitely
				if (isDestroyed) {
					return;
				}

				const compositionPluginState = api?.composition?.sharedState.currentState();
				const isShowingDiff = Boolean(
					api?.showDiff?.sharedState.currentState()?.isDisplayingChanges,
				);

				const syncBlockPlaceholderDecorations =
					!compositionPluginState?.isComposing && !isShowingDiff
						? sourceSyncBlockPlaceholderDecorations
						: undefined;
				const multiBodiedExtensionDecorations =
					!compositionPluginState?.isComposing && !isShowingDiff
						? multiBodiedExtensionPlaceholderDecorations
						: undefined;
				const syncBlockDecorationItems = syncBlockPlaceholderDecorations?.find() ?? [];
				const multiBodiedExtensionDecorationItems = multiBodiedExtensionDecorations?.find() ?? [];

				if (
					hasPlaceholder &&
					((placeholderText ?? '') ||
						placeholderPrompts ||
						placeholderADF ||
						contextPlaceholderADF) &&
					pos !== undefined &&
					!compositionPluginState?.isComposing &&
					!isShowingDiff
				) {
					const initialDelayWhenUserTypedAndDeleted = typedAndDeleted
						? TYPEWRITER_TYPED_AND_DELETED_DELAY
						: 0;
					// contextPlaceholderADF takes precedence over the global placeholderADF
					const placeholderAdfToUse = contextPlaceholderADF || placeholderADF;
					return createPlaceholderDecoration(
						editorState,
						placeholderText ?? '',
						placeholderPrompts,
						activeTypewriterTimeouts,
						pos,
						initialDelayWhenUserTypedAndDeleted,
						placeholderAdfToUse,
						showOnEmptyParagraph,
					).add(editorState.doc, [
						...syncBlockDecorationItems,
						...multiBodiedExtensionDecorationItems,
					]);
				}
				// These sets are already mapped in plugin state. Return a single
				// active set directly, and only rebuild when multiple sets must be merged.
				if (
					syncBlockDecorationItems.length > 0 &&
					multiBodiedExtensionDecorationItems.length === 0
				) {
					return syncBlockPlaceholderDecorations;
				}
				if (
					multiBodiedExtensionDecorationItems.length > 0 &&
					syncBlockDecorationItems.length === 0
				) {
					return multiBodiedExtensionDecorations;
				}
				const additionalDecorations = [
					...syncBlockDecorationItems,
					...multiBodiedExtensionDecorationItems,
				];
				return additionalDecorations.length > 0
					? DecorationSet.create(editorState.doc, additionalDecorations)
					: undefined;
			},
		},

		view() {
			let timeoutId: ReturnType<typeof setTimeout> | undefined;

			function startEmptyParagraphTimeout(editorView: EditorView) {
				if (timeoutId) {
					return;
				}
				timeoutId = setTimeout(() => {
					timeoutId = undefined;
					editorView.dispatch(
						editorView.state.tr.setMeta(pluginKey, {
							showOnEmptyParagraph: true,
						}),
					);
				}, EMPTY_PARAGRAPH_TIMEOUT_DELAY);
			}

			function destroyEmptyParagraphTimeout() {
				if (timeoutId) {
					clearTimeout(timeoutId);
					timeoutId = undefined;
				}
			}

			return {
				update(editorView, prevState) {
					if (withEmptyParagraph) {
						const prevPluginState = getPlaceholderState(prevState);
						const newPluginState = getPlaceholderState(editorView.state);

						// user start typing after move to an empty paragraph, clear timeout
						if (!newPluginState.canShowOnEmptyParagraph && timeoutId) {
							destroyEmptyParagraphTimeout();
						}
						// user move to an empty paragraph again, reset state to hide placeholder, and restart timeout
						else if (
							prevPluginState.canShowOnEmptyParagraph &&
							newPluginState.canShowOnEmptyParagraph &&
							newPluginState.pos !== prevPluginState.pos
						) {
							editorView.dispatch(
								editorView.state.tr.setMeta(pluginKey, {
									showOnEmptyParagraph: false,
								}),
							);
							destroyEmptyParagraphTimeout();
							startEmptyParagraphTimeout(editorView);
						}
						// user move to an empty paragraph (by click enter or move to an empty paragraph), start timeout
						else if (
							!prevPluginState.canShowOnEmptyParagraph &&
							newPluginState.canShowOnEmptyParagraph &&
							!newPluginState.showOnEmptyParagraph &&
							!timeoutId
						) {
							startEmptyParagraphTimeout(editorView);
						}
					}
				},

				destroy() {
					clearAllTypewriterTimeouts();

					destroyEmptyParagraphTimeout();

					isDestroyed = true;
				},
			};
		},
	});
}
